import { Navigate } from 'react-router-dom';
import { JSX, lazy, Suspense } from 'react';
import { ProtectedRoute } from '@/shared/components/ProtectedRoute';
import { APP_STRINGS } from '@/shared/constants';
import { useAuthContext } from '@/shared/contexts/AuthContext';
import { getRoleBasedRedirect } from '@/shared/lib/auth.utils';

// Login/Register stay eagerly loaded (they are the entry points)
import Login from '@/features/user/pages/Login';
import Register from '@/features/user/pages/Register';
import VerifyOtp from '@/features/user/pages/VerifyOtp';
import SetPassword from '@/features/user/pages/SetPassword';
import ForgotPassword from '@/features/user/pages/ForgotPassword';
import ResetPassword from '@/features/user/pages/ResetPassword';
import { PublicRoute } from '@/shared/components/PublicRoute';

// Lazy-loaded portal pages — each gets its own chunk
const UserDashboard = lazy(() => import('@/features/user/pages/UserDashboard'));
const UserProfile = lazy(() => import('@/features/user/pages/UserProfile'));
const PrivacyCenter = lazy(() => import('@/features/user/pages/PrivacyCenter'));
const AdminDashboard = lazy(
  () => import('@/features/admin/pages/AdminDashboard')
);
const UserManagement = lazy(
  () => import('@/features/admin/pages/UserManagement')
);
const ReviewFeedbackPage = lazy(
  () => import('@/features/admin/pages/ReviewFeedbackPage')
);
const KnowledgeManagement = lazy(
  () => import('@/features/admin/pages/KnowledgeManagement')
);
const DomainManagement = lazy(
  () => import('@/features/admin/pages/DomainManagement')
);
const AICostMonitoring = lazy(
  () => import('@/features/admin/pages/AICostMonitoring')
);
const ValidationControlPage = lazy(
  () => import('@/features/admin/pages/ValidationControlPage')
);
const CostControlsPage = lazy(
  () => import('@/features/admin/pages/CostControlsPage')
);
const ValidationConfigPage = lazy(
  () => import('@/features/admin/pages/ValidationConfigPage')
);
const DataRetentionPage = lazy(
  () => import('@/features/admin/pages/DataRetentionPage')
);
const AdversarialEvalPage = lazy(
  () => import('@/features/admin/pages/AdversarialEvalPage')
);
const AuditReportsPage = lazy(
  () => import('@/features/admin/pages/AuditReportsPage')
);
const ReviewerDashboard = lazy(
  () => import('@/features/reviewer/pages/ReviewerDashboard')
);
const HumanReviewDisclosure = lazy(
  () => import('@/features/user/pages/HumanReviewDisclosure')
);

function FullPageSpinner() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"></div>
        <p className="mt-4 text-muted-foreground">{APP_STRINGS.LOADING}</p>
      </div>
    </div>
  );
}

function RootRedirect() {
  const { user, isAuthenticated, isLoading } = useAuthContext();
  if (isLoading) return <FullPageSpinner />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return <Navigate to={getRoleBasedRedirect(user?.roles ?? [])} replace />;
}

export interface RouteConfig {
  path: string;
  element: JSX.Element;
}

export const routes: RouteConfig[] = [
  // Public routes — redirect to role portal when already authenticated
  {
    path: '/login',
    element: (
      <PublicRoute>
        <Login />
      </PublicRoute>
    ),
  },
  {
    path: '/register',
    element: (
      <PublicRoute>
        <Register />
      </PublicRoute>
    ),
  },
  {
    path: '/verify-otp',
    element: (
      <PublicRoute>
        <VerifyOtp />
      </PublicRoute>
    ),
  },
  {
    path: '/auth/set-password',
    element: (
      <PublicRoute>
        <SetPassword />
      </PublicRoute>
    ),
  },
  {
    path: '/auth/forgot-password',
    element: (
      <PublicRoute>
        <ForgotPassword />
      </PublicRoute>
    ),
  },
  {
    path: '/auth/reset-password',
    element: (
      <PublicRoute>
        <ResetPassword />
      </PublicRoute>
    ),
  },

  // Root: smart redirect to role-appropriate portal
  {
    path: '/',
    element: <RootRedirect />,
  },

  // Protected routes — User Portal
  {
    path: '/user',
    element: (
      <ProtectedRoute requiredRoles={['user']}>
        <Suspense fallback={<FullPageSpinner />}>
          <UserDashboard />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: '/user/profile',
    element: (
      <ProtectedRoute requiredRoles={['user']}>
        <Suspense fallback={<FullPageSpinner />}>
          <UserProfile />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: '/user/privacy',
    element: (
      <ProtectedRoute requiredRoles={['user']}>
        <Suspense fallback={<FullPageSpinner />}>
          <PrivacyCenter />
        </Suspense>
      </ProtectedRoute>
    ),
  },

  // Protected routes — Admin Portal
  {
    path: '/admin',
    element: <Navigate to="/admin/users" replace />,
  },
  {
    path: '/admin/users',
    element: (
      <ProtectedRoute requiredRoles={['admin']}>
        <Suspense fallback={<FullPageSpinner />}>
          <UserManagement />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/sessions',
    element: (
      <ProtectedRoute requiredRoles={['admin']}>
        <Suspense fallback={<FullPageSpinner />}>
          <AdminDashboard />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/reviews',
    element: (
      <ProtectedRoute requiredRoles={['admin']}>
        <Suspense fallback={<FullPageSpinner />}>
          <ReviewFeedbackPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/knowledge',
    element: (
      <ProtectedRoute requiredRoles={['admin']}>
        <Suspense fallback={<FullPageSpinner />}>
          <KnowledgeManagement />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/knowledge-domains',
    element: (
      <ProtectedRoute requiredRoles={['admin']}>
        <Suspense fallback={<FullPageSpinner />}>
          <DomainManagement />
        </Suspense>
      </ProtectedRoute>
    ),
  },

  {
    path: '/admin/ai-cost',
    element: (
      <ProtectedRoute requiredRoles={['admin']}>
        <Suspense fallback={<FullPageSpinner />}>
          <AICostMonitoring />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/validation',
    element: (
      <ProtectedRoute requiredRoles={['admin']}>
        <Suspense fallback={<FullPageSpinner />}>
          <ValidationControlPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/cost-controls',
    element: (
      <ProtectedRoute requiredRoles={['admin']}>
        <Suspense fallback={<FullPageSpinner />}>
          <CostControlsPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },

  {
    path: '/admin/validation-config',
    element: (
      <ProtectedRoute requiredRoles={['admin']}>
        <Suspense fallback={<FullPageSpinner />}>
          <ValidationConfigPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },

  {
    path: '/admin/data-retention',
    element: (
      <ProtectedRoute requiredRoles={['admin']}>
        <Suspense fallback={<FullPageSpinner />}>
          <DataRetentionPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },

  {
    path: '/admin/adversarial-eval',
    element: (
      <ProtectedRoute requiredRoles={['admin']}>
        <Suspense fallback={<FullPageSpinner />}>
          <AdversarialEvalPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/audit-reports',
    element: (
      <ProtectedRoute requiredRoles={['admin']}>
        <Suspense fallback={<FullPageSpinner />}>
          <AuditReportsPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },

  // Protected routes — Reviewer Portal
  {
    path: '/reviewer',
    element: <Navigate to="/reviewer/dashboard" replace />,
  },
  {
    path: '/reviewer/dashboard',
    element: (
      <ProtectedRoute requiredRoles={['reviewer']}>
        <Suspense fallback={<FullPageSpinner />}>
          <ReviewerDashboard />
        </Suspense>
      </ProtectedRoute>
    ),
  },

  // Public legal/disclosure pages — no auth guard
  {
    path: '/human-review-disclosure',
    element: (
      <Suspense fallback={<FullPageSpinner />}>
        <HumanReviewDisclosure />
      </Suspense>
    ),
  },

  // Catch-all redirect
  {
    path: '*',
    element: <Navigate to="/" replace />,
  },
];
